import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Show,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiChevronsDown,
  FiChevronsUp,
} from 'react-icons/fi';

import {
  ExpertRatingsPreview,
  PreviewSectionPanel,
  useMarketingRatings,
} from '@ww/marketing-preview';
import MarketingPageShell from '../components/MarketingPageShell';
import { APP_RATINGS_URL, SIGNUP_URL } from '../config/api';
import { SITE_URL } from '../components/PageMeta';
import { warmAppNavigationLinks } from '../utils/appLinkHints';
import { buildSignupUrl, trackMarketingEvent, trackSignupClick } from '../utils/analytics';

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Ratings', path: '/ratings' },
];

const RATINGS_DESCRIPTION =
  'Browse independent expert performance ratings — ranked by profit rating (PR), ROI, and WCS with verified pick tracking. Public page updates daily; the app refreshes about every hour.';

const SCROLL_EDGE_PX = 48;

function formatRatingsUpdatedAt(iso: string): string {
  return `${new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })} UTC`;
}

export default function RatingsPage() {
  const { previewRows, isLoading, updatedAt } = useMarketingRatings();
  const [tableReady, setTableReady] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomMarkerRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const usePageScroll = useBreakpointValue({ base: true, md: false }) ?? true;

  useEffect(() => {
    warmAppNavigationLinks(
      SIGNUP_URL.replace(/\/signup$/, ''),
      ['/signup', '/login'],
    );
  }, []);

  useEffect(() => {
    trackMarketingEvent('ratings_page_view');
  }, []);

  useEffect(() => {
    if (isLoading) {
      setTableReady(false);
      return;
    }

    const ready = () => setTableReady(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(ready);
      return () => window.cancelIdleCallback(id);
    }

    const id = setTimeout(ready, 0);
    return () => clearTimeout(id);
  }, [isLoading]);

  const updateScrollAffordances = useCallback(() => {
    if (usePageScroll) {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      setCanScrollUp(scrollTop > SCROLL_EDGE_PX);
      setCanScrollDown(scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_PX);
      return;
    }

    const el = scrollRef.current;
    if (!el) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = el;
    setCanScrollUp(scrollTop > SCROLL_EDGE_PX);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_PX);
  }, [usePageScroll]);

  useEffect(() => {
    if (!tableReady) return;

    updateScrollAffordances();

    const onScroll = () => updateScrollAffordances();
    const scrollTarget = usePageScroll ? window : scrollRef.current;
    scrollTarget?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateScrollAffordances);

    const observer =
      !usePageScroll &&
      typeof ResizeObserver !== 'undefined' &&
      scrollRef.current
        ? new ResizeObserver(updateScrollAffordances)
        : null;
    if (scrollRef.current) {
      observer?.observe(scrollRef.current);
    }

    return () => {
      scrollTarget?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateScrollAffordances);
      observer?.disconnect();
    };
  }, [tableReady, previewRows.length, updateScrollAffordances, usePageScroll]);

  const scrollToTop = () => {
    if (usePageScroll) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const scrollToBottom = () => {
    if (usePageScroll) {
      bottomMarkerRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      return;
    }
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  };

  const scrollByPage = (direction: 'up' | 'down') => {
    if (usePageScroll) {
      const delta = window.innerHeight * 0.85 * (direction === 'down' ? 1 : -1);
      window.scrollBy({ top: delta, behavior: 'auto' });
      return;
    }
    const el = scrollRef.current;
    if (!el) return;
    const delta = el.clientHeight * 0.85 * (direction === 'down' ? 1 : -1);
    el.scrollBy({ top: delta, behavior: 'auto' });
  };

  const showTable = !isLoading && tableReady;

  return (
    <MarketingPageShell
      fullTitle="Expert Ratings | WagerWatchdog"
      description={RATINGS_DESCRIPTION}
      article={{
        ogImageAlt:
          'WagerWatchdog expert ratings leaderboard — independent performance ratings for sports betting experts',
      }}
      breadcrumbs={BREADCRUMBS}
      h1="Expert"
      h1Accent="Ratings"
      compactHero
      lead="Sort by PR, ROI, WCS, and more. Public page updates daily; the app refreshes about every hour."
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Expert Ratings | WagerWatchdog',
          description: RATINGS_DESCRIPTION,
          url: `${SITE_URL}/ratings`,
        },
      ]}
    >
      <Box position="relative" minW={0} maxW="100%" overflowX="hidden">
        <PreviewSectionPanel
          eyebrow=""
          title=""
          description=""
          compactContent
          hideHeader
        >
          {showTable ? (
            <>
              <Flex
                justify="space-between"
                align="center"
                mb={2}
                gap={3}
                flexWrap="wrap"
              >
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                  color="green.300"
                >
                  Live ratings
                </Text>
                {updatedAt ? (
                  <Text fontSize="xs" color="gray.500">
                    Last updated {formatRatingsUpdatedAt(updatedAt)}
                  </Text>
                ) : null}
              </Flex>
              <Box
              ref={usePageScroll ? undefined : scrollRef}
              maxH={usePageScroll ? undefined : { md: '72vh' }}
              overflowY={usePageScroll ? 'visible' : 'auto'}
              overflowX={usePageScroll ? 'hidden' : 'auto'}
              overscrollBehavior={usePageScroll ? undefined : 'contain'}
              sx={
                usePageScroll
                  ? undefined
                  : {
                      scrollBehavior: 'auto',
                      WebkitOverflowScrolling: 'touch',
                    }
              }
            >
              <ExpertRatingsPreview
                rows={previewRows}
                isLoading={false}
                showHeading={false}
                rowHref={buildSignupUrl(SIGNUP_URL)}
                compact
                stickyHeader={!usePageScroll}
                inScrollContainer={!usePageScroll}
                sortable
                prominentHeaders
                hideRankExplainer
              />
              <Text fontSize="xs" color="gray.500" mt={3} lineHeight="1.5">
                Public ratings refresh once per day;{' '}
                <Text
                  as="a"
                  href={APP_RATINGS_URL}
                  color="primary.300"
                  fontWeight="medium"
                  _hover={{ color: 'primary.200', textDecoration: 'underline' }}
                >
                  open the app
                </Text>{' '}
                for hourly updates.
              </Text>
              <Box ref={bottomMarkerRef} h="1px" aria-hidden />
            </Box>
            </>
          ) : (
            <Flex justify="center" py={16}>
              <Spinner size="lg" color="primary.400" />
            </Flex>
          )}
        </PreviewSectionPanel>

        {showTable && (canScrollUp || canScrollDown) ? (
          <HStack
            position="fixed"
            bottom={{ base: 'max(1rem, env(safe-area-inset-bottom))', md: 6 }}
            left={{ base: 3, md: 'auto' }}
            right={{ base: 3, md: 6 }}
            zIndex={30}
            spacing={2}
            bg="gray.800"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="full"
            p={{ base: 1.5, md: 1.5 }}
            boxShadow="lg"
            justify={{ base: 'center', md: 'flex-start' }}
            overflow="hidden"
            minW={0}
          >
            {canScrollUp ? (
              <>
                <IconButton
                  aria-label="Jump to top of ratings"
                  icon={<FiChevronsUp />}
                  size={{ base: 'md', md: 'sm' }}
                  minW={{ base: '40px', md: '32px' }}
                  minH={{ base: '40px', md: '32px' }}
                  flexShrink={0}
                  variant="ghost"
                  color="gray.300"
                  onClick={scrollToTop}
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                />
                <Show above="md">
                  <IconButton
                    aria-label="Scroll up one screen"
                    icon={<FiChevronUp />}
                    size="sm"
                    variant="ghost"
                    color="gray.300"
                    onClick={() => scrollByPage('up')}
                    _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                  />
                </Show>
              </>
            ) : null}
            {canScrollDown ? (
              <>
                <Show above="md">
                  <IconButton
                    aria-label="Scroll down one screen"
                    icon={<FiChevronDown />}
                    size="sm"
                    variant="ghost"
                    color="gray.300"
                    onClick={() => scrollByPage('down')}
                    _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                  />
                </Show>
                <Button
                  size={{ base: 'sm', md: 'sm' }}
                  minH={{ base: '40px', md: '32px' }}
                  minW={0}
                  variant="primary"
                  leftIcon={<FiChevronsDown />}
                  onClick={scrollToBottom}
                  px={{ base: 3, md: 4 }}
                  borderRadius="full"
                  flex={{ base: 1, md: undefined }}
                  flexShrink={1}
                >
                  Bottom
                </Button>
              </>
            ) : null}
          </HStack>
        ) : null}
      </Box>

      <VStack spacing={3} pt={8} pb={{ base: 28, md: 8 }} px={4} textAlign="center">
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="bold"
          color="white"
          maxW="520px"
        >
          This page shows the public expert ratings preview.
        </Text>
        <Text color="gray.400" maxW="560px" fontSize={{ base: 'sm', md: 'md' }}>
          Create a free account for full expert ratings, profiles, and verified
          pick tracking. In the app, scores refresh about every hour as new picks
          are processed. Experts:{' '}
          <Text as="span" color="gray.300" fontWeight="medium">
            claim your profile
          </Text>{' '}
          to connect your brand to this record.
        </Text>
        <HStack spacing={3} pt={1} justify="center" flexWrap="wrap">
          <Button
            as="a"
            href={buildSignupUrl(SIGNUP_URL)}
            rel="noopener noreferrer"
            variant="primary"
            size="lg"
            px={10}
            _hover={{ textDecoration: 'none', transform: 'translateY(-1px)' }}
            transition="transform 0.15s ease"
            onClick={() => trackSignupClick('ratings_cta')}
          >
            Create free account
          </Button>
          <Button
            as="a"
            href="/claim"
            variant="outline"
            size="lg"
            px={8}
            borderColor="whiteAlpha.300"
            color="white"
            _hover={{ textDecoration: 'none', bg: 'whiteAlpha.100' }}
          >
            Claim your profile
          </Button>
        </HStack>
      </VStack>
    </MarketingPageShell>
  );
}
